import { InputTypes, SelectFieldOptions } from "@picovico/ui";
import { Params, PrimitivesType } from "custom-types";
import { isArray } from "lodash";
import * as yup from "yup";
import { FieldSchema } from "@picovico/base";

export interface Validation {
  pattern: string;
  error_message: string;
}
export interface APIFormField {
  label: string;
  length: number;
  api_key: string;
  options: SelectFieldOptions<unknown>;
  data_api: null;
  required: boolean;
  help_text: string;
  field_type: InputTypes;
  input_type: InputTypes;
  validation: Validation[];
  isDisabled: boolean;
  is_multi: boolean;
  multiple_error: boolean;
  type?: string;
  name?: string;
  id?: string;
  loadOptions?: (
    inputValue: string,
    queryParam?: Params,
  ) => Promise<SelectFieldOptions<unknown>> | void;
}

export interface ParsedForm {
  fields: FieldSchema[];
  initialValues: Record<string, PrimitivesType>;
}

export interface AppFormParserProps {
  initialValues: Record<string, PrimitivesType>;
}

export class AppFormParser {
  private apiFormSchema: APIFormField[];
  private props: AppFormParserProps;

  constructor(apiFormSchema: APIFormField[], props: AppFormParserProps) {
    this.apiFormSchema = apiFormSchema;
    this.props = props;
  }

  private convertStringValidation(validation: Validation[]): yup.StringSchema {
    let validationSchema = yup.string();

    validation.forEach((rule) => {
      if (rule.pattern) {
        validationSchema = validationSchema?.matches(
          new RegExp(rule.pattern),
          rule.error_message,
        );
      }
    });

    if (validation.some((rule) => rule.pattern)) {
      validationSchema = validationSchema.required();
    }

    return validationSchema;
  }

  convertArrayValidation(
    validation: Validation[],
  ): yup.ArraySchema<
    Array<string | undefined> | undefined,
    yup.AnyObject,
    "",
    ""
  > {
    const itemValidation = yup.string();

    for (let i = 0; i < validation.length; i++) {
      const rule = validation[i];
      if (rule.pattern) {
        itemValidation.matches(new RegExp(rule.pattern), rule.error_message);
      }
    }

    const arrayValidation = yup.array().of(itemValidation);

    return arrayValidation;
  }
  parse() {
    const { initialValues = {} } = this.props;
    const parsedForm: ParsedForm = {
      fields: [],
      initialValues: { ...initialValues },
    };
    (isArray(this.apiFormSchema) ? this.apiFormSchema : [])?.forEach(
      (field) => {
        const parsedField = this.parseField(field);
        parsedForm.fields.push(parsedField);
      },
    );

    return parsedForm;
  }

  private getValidationYupSchema(
    field: APIFormField,
  ): yup.AnySchema | undefined {
    if (!field.validation?.length) return undefined;

    let validationFieldSchema;
    if (field.is_multi) {
      validationFieldSchema = this.convertArrayValidation(field.validation);
    } else {
      validationFieldSchema = this.convertStringValidation(field.validation);
    }

    if (field.length) {
      validationFieldSchema
        .max(field.length, `Max length is ${field.length}`)
        .required(`This field is required.`);
    }

    if (field.required) {
      validationFieldSchema.required(`This field is required.`);
    }

    return validationFieldSchema;
  }

  private parseField(field: APIFormField): FieldSchema {
    const {
      label,
      api_key,
      options,
      help_text,
      isDisabled,
      field_type,
      input_type,
      required,
      is_multi,
      type,
      name,
      id,
      loadOptions,
    } = field;
    return {
      id: api_key ?? id,
      name: api_key ?? name,
      label,
      placeholder: help_text,
      type: input_type ?? field_type ?? type,
      options: isArray(options) ? options : [],
      defaultValue: "",
      required,
      disabled: isDisabled ?? false,
      isMulti: is_multi,
      validation: this.getValidationYupSchema(field),
      loadOptions: loadOptions,
    };
  }
}
